/* global Alpine */

/**
 * Componente Alpine per la pagina Checkout.
 * Gestisce form contatto/partecipante, aggiornamento carrello (PATCH)
 * e creazione prenotazione. Il pagamento avviene sulla pagina booking.
 */
export default function checkoutPage() {
  document.addEventListener('alpine:init', () => {
    Alpine.data('checkoutPage', () => ({
      isSubmitting: false,
      error: null,
      hasDifferentParticipant: false,
      wantsInvoice: false,
      acceptedTerms: false,
      termsError: false,
      editingContact: false,
      invoiceFieldErrors: {},

      // Step del flusso per feedback visivo
      // idle | creating_booking | done | error
      paymentStep: 'idle',

      contact: {
        firstname: '',
        lastname: '',
        email: '',
        phone_prefix: '39',
        phone: '',
        notes: '',
      },

      participant: {
        firstname: '',
        lastname: '',
        email: '',
        phone_prefix: '39',
        phone: '',
      },

      invoice: {
        subject_type: 'private',
        holder: '',
        firstname: '',
        lastname: '',
        vatnumber: '',
        taxcode: '',
        address: '',
        streetnumber: '',
        zipcode: '',
        city: '',
        province: '',
        country: 'IT',
        sdicode: '',
        pec: '',
      },

      config: {},

      init() {
        const raw = this.$el.getAttribute('data-checkout-config')
        if (raw) {
          try {
            this.config = JSON.parse(raw)
          } catch {
            this.config = {}
          }
        }

        if (this.config.contact) {
          const c = this.config.contact
          this.contact.firstname = c.firstname || ''
          this.contact.lastname = c.lastname || ''
          this.contact.email = c.email || ''
          this.contact.phone = c.phone || ''
          this.contact.notes = c.notes || ''
          const prefix = String(c.phone_prefix || '+39').replace('+', '')
          this.contact.phone_prefix = prefix || '39'
        }

        this.editingContact = !this.contactHasData()
        this.prefillInvoiceFromContact()
      },

      get isInvoicePrivate() {
        return this.invoice.subject_type === 'private'
      },

      get isInvoicePa() {
        return this.invoice.subject_type === 'pa'
      },

      get isInvoiceCompany() {
        return (
          this.invoice.subject_type === 'company' ||
          this.invoice.subject_type === 'freelancer'
        )
      },

      onInvoiceSubjectTypeChange() {
        if (this.invoice.subject_type === 'pa') {
          this.invoice.country = 'IT'
          this.invoice.vatnumber = ''
          this.invoice.pec = ''
        }
      },

      prefillInvoiceFromContact() {
        const prefill = this.config.invoice?.prefill || {}
        const name = (
          (this.contact.firstname + ' ' + this.contact.lastname).trim() ||
          prefill.holder ||
          ''
        ).trim()
        if (!name) {
          return
        }

        this.invoice.holder = name
        this.invoice.firstname =
          this.contact.firstname || prefill.firstname || ''
        this.invoice.lastname = this.contact.lastname || prefill.lastname || ''
      },

      contactHasData() {
        return !!(
          this.contact.firstname.trim() &&
          this.contact.lastname.trim() &&
          this.contact.email.trim() &&
          this.contact.phone.trim()
        )
      },

      get contactPhoneFormatted() {
        return '+' + this.contact.phone_prefix + ' ' + this.contact.phone
      },

      cancelContactEdit() {
        const c = this.config.contact || {}
        this.contact.firstname = c.firstname || ''
        this.contact.lastname = c.lastname || ''
        this.contact.email = c.email || ''
        this.contact.phone = c.phone || ''
        this.contact.notes = c.notes || ''
        this.contact.phone_prefix =
          String(c.phone_prefix || '+39').replace('+', '') || '39'
        this.editingContact = false
      },

      async confirmContactEdit() {
        this.error = null
        if (!this.validateContact()) return

        this.isSubmitting = true
        const strings = this.config.strings || {}

        try {
          const endpoint =
            this.config.updateCartEndpoint || '/api?endpoint=update-cart'
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(this.buildUpdatePayload()),
          })

          const data = await res.json()
          if (!res.ok || data.error) {
            this.error =
              data.error || strings.updateError || 'Errore aggiornamento dati.'
            this.isSubmitting = false
            return
          }

          this.editingContact = false
        } catch {
          this.error = strings.updateError || 'Errore aggiornamento dati.'
        } finally {
          this.isSubmitting = false
        }
      },

      validateContact() {
        const s = this.config.strings || {}
        if (!this.contact.firstname.trim() || !this.contact.lastname.trim()) {
          this.error = s.requiredField || 'Campo obbligatorio.'
          return false
        }
        if (!this.contact.email.trim() || !this.contact.email.includes('@')) {
          this.error = s.invalidEmail || 'Email non valida.'
          return false
        }
        if (!this.contact.phone.trim()) {
          this.error = s.requiredField || 'Campo obbligatorio.'
          return false
        }
        if (
          this.hasDifferentParticipant &&
          (!this.participant.firstname.trim() ||
            !this.participant.lastname.trim())
        ) {
          this.error = s.requiredField || 'Campo obbligatorio.'
          return false
        }
        if (this.wantsInvoice && !this.validateInvoice()) {
          return false
        }
        return true
      },

      validateInvoice() {
        const strings = this.config.invoice?.strings || {}
        const errors = {}
        const f = this.invoice

        if (!f.subject_type) {
          errors.subject_type = strings.required || 'Campo obbligatorio.'
        }

        if (!/^[A-Za-z]{2}$/.test((f.country || '').trim())) {
          errors.country = strings.invalidCountry || 'Paese non valido.'
        }

        if (f.pec && !this.isInvoiceEmail(f.pec)) {
          errors.pec = strings.invalidEmail || 'Email non valida.'
        }

        if (f.sdicode && !/^[A-Za-z0-9]{6,7}$/.test(f.sdicode.trim())) {
          errors.sdicode = strings.invalidSdi || 'Codice SDI non valido.'
        }

        if (f.subject_type === 'private') {
          if (!f.firstname.trim()) {
            errors.firstname = strings.required || 'Campo obbligatorio.'
          }
          if (!f.lastname.trim()) {
            errors.lastname = strings.required || 'Campo obbligatorio.'
          }
          if (!f.taxcode.trim()) {
            errors.taxcode = strings.required || 'Campo obbligatorio.'
          }
        } else if (f.subject_type === 'pa') {
          f.country = 'IT'
          if (!f.holder.trim()) {
            errors.holder = strings.required || 'Campo obbligatorio.'
          }
          if (!f.taxcode.trim()) {
            errors.taxcode = strings.required || 'Campo obbligatorio.'
          }
          if (!f.sdicode.trim()) {
            errors.sdicode = strings.required || 'Campo obbligatorio.'
          } else if (!/^[A-Za-z0-9]{6,7}$/.test(f.sdicode.trim())) {
            errors.sdicode =
              strings.invalidPaOfficeCode ||
              strings.invalidSdi ||
              'Codice univoco ufficio non valido.'
          }
        } else {
          if (!f.holder.trim()) {
            errors.holder = strings.required || 'Campo obbligatorio.'
          }
          if (!f.vatnumber.trim() && !f.taxcode.trim()) {
            errors.vatnumber = strings.needVatOrTax || 'P.IVA o codice fiscale.'
          }
          if (!f.sdicode.trim() && !f.pec.trim()) {
            errors.sdicode = strings.needSdiOrPec || 'SDI o PEC.'
          }
        }

        this.invoiceFieldErrors = errors
        if (Object.keys(errors).length > 0) {
          this.error =
            strings.required ||
            'Compila tutti i campi obbligatori della fattura.'
          return false
        }

        return true
      },

      isInvoiceEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      },

      buildUpdatePayload() {
        const data = {
          contact_firstname: this.contact.firstname.trim(),
          contact_lastname: this.contact.lastname.trim(),
          contact_email: this.contact.email.trim(),
          contact_phone_prefix: parseInt(this.contact.phone_prefix, 10),
          contact_phone: this.contact.phone.trim(),
          notes: this.contact.notes.trim(),
          participant_different: this.hasDifferentParticipant ? 1 : 0,
          tc_accepted: this.acceptedTerms ? 1 : 0,
          tc_version: this.config.tcVersion || '1.0',
        }

        if (this.hasDifferentParticipant) {
          data.participant_firstname = this.participant.firstname.trim()
          data.participant_lastname = this.participant.lastname.trim()
          data.participant_email = this.participant.email.trim()
          data.participant_phone_prefix = parseInt(
            this.participant.phone_prefix,
            10
          )
          data.participant_phone = this.participant.phone.trim()
        }

        return data
      },

      get paymentStepLabel() {
        const map = {
          creating_booking: 'Creazione prenotazione...',
          saving_invoice: 'Salvataggio dati fatturazione...',
          done: 'Completato!',
        }
        return map[this.paymentStep] || null
      },

      async syncCartBeforeBooking() {
        const endpoint =
          this.config.updateCartEndpoint || '/api?endpoint=update-cart'
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(this.buildUpdatePayload()),
        })
        const data = await res.json()
        if (!res.ok || data.error) {
          const strings = this.config.strings || {}
          this.error =
            data.error || strings.updateError || 'Errore aggiornamento dati.'
          this.paymentStep = 'error'
          this.isSubmitting = false
          throw new Error('cart_sync_failed')
        }
      },

      async saveInvoiceData(bookingSid) {
        const strings = this.config.invoice?.strings || {}
        const endpoint =
          this.config.invoice?.saveEndpoint || '/api?endpoint=save-invoice-data'

        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              booking_sid: bookingSid,
              invoice_requested: true,
              ...this.invoice,
            }),
          })
          const data = await res.json()
          if (!res.ok || data.error) {
            this.error =
              data.error ||
              strings.saveError ||
              'Errore durante il salvataggio dei dati di fatturazione.'
            this.paymentStep = 'error'
            this.isSubmitting = false
            return false
          }
          return true
        } catch {
          this.error =
            strings.saveError ||
            'Errore durante il salvataggio dei dati di fatturazione.'
          this.paymentStep = 'error'
          this.isSubmitting = false
          return false
        }
      },

      async submitBooking() {
        if (this.isSubmitting) return

        this.error = null
        const strings = this.config.strings || {}

        if (this.editingContact) {
          this.error = 'Conferma i dati di contatto prima di procedere.'
          return
        }

        if (!this.validateContact()) return

        if (!this.acceptedTerms) {
          this.termsError = true
          this.error =
            strings.termsRequired ||
            'Devi accettare i Termini e Condizioni per procedere.'
          this.$refs.termsBox?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
          return
        }

        this.isSubmitting = true

        try {
          this.paymentStep = 'creating_booking'
          await this.syncCartBeforeBooking()
          const bookingResult = await this.createBooking()
          if (!bookingResult) return

          if (this.wantsInvoice) {
            this.paymentStep = 'saving_invoice'
            const saved = await this.saveInvoiceData(bookingResult.booking_sid)
            if (!saved) return
          }

          this.paymentStep = 'done'
          const bookingSid = bookingResult.booking_sid
          const prefix = this.config.bookingUrlPrefix || '/'
          window.location.href =
            prefix + 'booking/' + encodeURIComponent(bookingSid)
        } catch {
          this.error =
            strings.bookingError ||
            'Errore durante la creazione della prenotazione. Riprova.'
          this.paymentStep = 'error'
          this.isSubmitting = false
        }
      },

      async createBooking() {
        const strings = this.config.strings || {}
        const endpoint =
          this.config.createBookingEndpoint || '/api?endpoint=create-booking'

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({}),
        })

        const data = await res.json()
        if (!res.ok || data.error) {
          this.error =
            data.error ||
            strings.bookingError ||
            'Errore durante la prenotazione.'
          this.paymentStep = 'error'
          this.isSubmitting = false
          return null
        }

        if (data.result?.price_changed) {
          const diff = Number(data.result.price_changed_amount || 0)
          const sign = diff > 0 ? '+' : ''
          this.error = `Il prezzo è cambiato di ${sign}${diff.toFixed(2)} €. Verifica il nuovo totale e riprova.`
          this.paymentStep = 'error'
          this.isSubmitting = false
          return null
        }

        return data.result
      },
    }))
  })
}
